import React from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

export interface DashboardAlert {
  type: string;
  title: string;
  message: string;
}

interface AlertActionBannerProps {
  alerts: DashboardAlert[];
}

export const AlertActionBanner: React.FC<AlertActionBannerProps> = ({ alerts }) => {
  const critical = alerts.filter((a) => a.type === 'error' || a.type === 'warning');
  if (critical.length === 0) return null;

  const hasError = critical.some((a) => a.type === 'error');
  const accentBar = hasError ? 'bg-[var(--destructive)]' : 'bg-[var(--mymoolah-blue)]';
  const iconWrap = hasError
    ? 'bg-red-50 text-[var(--destructive)]'
    : 'bg-[#2D8CCA]/10 text-[var(--mymoolah-blue)]';
  const Icon = hasError ? ShieldAlert : AlertTriangle;

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-[var(--portal-shadow)]"
      role="alert"
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${accentBar}`} aria-hidden />
      <div className="flex items-start gap-4 px-5 py-4 pl-6">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-semibold tracking-tight text-[var(--foreground)]">
            {hasError ? 'Immediate attention' : 'Action required'}
          </p>
          <ul className="mt-1.5 space-y-1">
            {critical.map((alert, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-[var(--muted-foreground)]"
              >
                <span
                  className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${hasError ? 'bg-[var(--destructive)]' : 'bg-[var(--mymoolah-blue)]'}`}
                  aria-hidden
                />
                <span>{alert.message}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
